import { HStack } from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";

import { MotionScaleFade } from "@/common/components/MotionComponents";
import SimpleSelect from "@/common/components/SimpleSelect";
import { SwitchButtonGroup } from "@/common/components/SwitchButtonGroup";
import {
  TableDisplayMode,
  useTableDisplayModeParam,
} from "@/common/hooks/useTableDisplayMode";

export type GroupingColumn<T extends string = string> = {
  id: T;
  label: string;
};

type Props<T extends string = string> = {
  groupingColumns?: GroupingColumn<T>[];
  groupingColumn?: GroupingColumn<T>["id"];
  onGroupingColumnChange?: (column?: GroupingColumn<T>["id"]) => void;
};

export function TableDisplayModePicker<T extends string = string>({
  groupingColumns,
  groupingColumn,
  onGroupingColumnChange,
}: Props<T>) {
  const [displayMode, setDisplayMode] = useTableDisplayModeParam();

  const buttons: Array<{ id: string; label: string }> = [
    { id: "tree", label: "Tree" },
    { id: "list", label: "List" },
  ];

  // Add Group by button if grouping columns are provided
  if (groupingColumns) {
    buttons.unshift({ id: "group", label: "Group" });
  }

  return (
    <HStack spacing={2}>
      <AnimatePresence initial={false}>
        {displayMode === "group" && groupingColumns && groupingColumn && (
          <MotionScaleFade origin="right">
            <SimpleSelect
              options={groupingColumns.map((column) => ({
                value: column.id,
                label: column.label,
              }))}
              placeholder="Select column..."
              size="sm"
              value={groupingColumn}
              onChange={(value) => {
                onGroupingColumnChange?.(value);
              }}
            />
          </MotionScaleFade>
        )}
      </AnimatePresence>
      <SwitchButtonGroup
        aria-label="Flag display mode"
        buttons={buttons}
        size="sm"
        value={displayMode}
        onChange={(mode) => {
          setDisplayMode(mode as TableDisplayMode);
        }}
      />
    </HStack>
  );
}
